import { useState, type CSSProperties } from 'react';
import { Briefcase, MapPin, Pencil, Plus, TreePalm } from 'lucide-react';
import { StatusChip, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { Button } from '@scripsteam/scrips-signal-ds';
import {
  useAppointmentProfileList,
  useAppointmentTemplateList,
  useCalendarSettingsList,
  useDoctorWorkingHourById,
  useDoctorWorkingHoursList,
  usePractitionerById,
  usePractitionerPractices,
} from '@/settings/services';
import { WorkingHoursEditDialog } from '../schedule/working-hours-edit-dialog';
import { CalendarSettingsEditDialog } from '../schedule/calendar-settings-edit-dialog';
import { AppointmentTemplateEditDialog } from '../schedule/appointment-template-edit-dialog';
import { AppointmentProfileEditDialog } from '../schedule/appointment-profile-edit-dialog';
import { flutterColorToCss } from '../schedule/appointment-profile-color';
import { formatTemplateTime } from '../schedule/appointment-template-time';
import type {
  AppointmentProfileEntry,
  AppointmentTemplateEntry,
  CalendarSettings,
  DoctorWorkingHoursListItem,
  DoctorWorkingHoursListSlot,
} from '@/types/settings-api';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Practitioner Schedule tab — PROD-762 (complete).
 *
 * Switched from the denormalised `practiceWorkingHours` summary on the
 * doctor record to the source-of-truth list endpoint:
 *
 *   GET /api/Doctor/WorkingHoursList/{doctorSetupId}
 *
 * All four sub-surfaces are wired with list + Add + Edit (+ delete confirm):
 *   - Working Hours      — WorkingHoursEditDialog (GET-by-id via
 *     `useDoctorWorkingHourById` + the split-time-string slot grammar)
 *   - Appointment Profiles  — AppointmentProfileEditDialog
 *   - Appointment Templates — AppointmentTemplateEditDialog
 *   - Calendar Settings     — CalendarSettingsEditDialog
 *
 * Wire-shape notes (verified PROD-762 PR-A cURL 2026-05-15):
 *   - Top-level id on the list is `workingHoursId` (PLURAL — distinct
 *     from the singular `workingHourId` used by the PUT body).
 *   - Slot times are combined strings (e.g. `"08:00 AM"`), not split
 *     hour+minute fields like the PUT body uses.
 *   - Dates are `dd-MM-yyyy` on this surface; PUT body uses no-Z ISO.
 *   - `weedayId` is typo'd verbatim — matches the convention from
 *     `WorkingHourSummary` elsewhere in the settings module.
 */
export interface PractitionerScheduleTabProps {
  doctorSetupId: string;
}

export function PractitionerScheduleTab({
  doctorSetupId,
}: PractitionerScheduleTabProps) {
  const { data, isLoading, error } = useDoctorWorkingHoursList(doctorSetupId);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCalendarSettingsId, setEditingCalendarSettingsId] = useState<
    string | null
  >(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null,
  );
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);

  // Lazy-fetch the canonical record only when the user clicks Edit.
  // Path quirk: GET uses /Doctor/WorkingHours/{id} (PLURAL); PUT uses
  // /Doctor/WorkingHour/{id} (SINGULAR). See PROD-819 for the backend
  // confirmation on the singular PUT path.
  const editingQuery = useDoctorWorkingHourById(editingId);

  // Cross-tab cache dependency (per @samer PR #132 review pattern):
  //   Pull the practitioner's orgnizationId from the cached Personal
  //   tab record. Same cache key the Billing tab uses. If the Personal
  //   tab hasn't loaded yet (deep link to Schedule), the hook fires
  //   its own GET silently.
  //
  //   Note: `orgnizationId` (typo'd) is the verbatim wire field on
  //   `Practitioner` — not `organizationId`. Don't auto-correct.
  const { data: practitioner } = usePractitionerById(doctorSetupId);
  const orgId = practitioner?.orgnizationId ?? null;
  const apptProfilesQuery = useAppointmentProfileList(doctorSetupId, orgId);
  const apptTemplatesQuery = useAppointmentTemplateList(doctorSetupId, orgId);
  const calendarSettingsQuery = useCalendarSettingsList(doctorSetupId, orgId);
  // Cross-reference calendar settings entries to practice names from
  // the cached practitioner-practices list (shipped PR-763). Falls
  // back to a GUID prefix if the cache hasn't populated.
  const practicesQuery = usePractitionerPractices(doctorSetupId);

  // Cross-reference templates against profiles: each template links to
  // an appointmentProfileId; build a lookup map so the template card
  // can display the profile name instead of the bare GUID.
  const profileNameById = new Map<string, string>(
    (apptProfilesQuery.data?.createAppointmentProfileRequest ?? []).map(
      (p) => [p.id, p.profileName ?? ''],
    ),
  );

  // Cross-reference calendar settings to practice names from the
  // practitioner-practices cache (PROD-763). Each calendar settings
  // entry has a practiceId; map to the practice name for display.
  const practiceNameById = new Map<string, string>(
    (practicesQuery.data ?? [])
      .filter((p) => p.practiceSetupId)
      .map((p) => [p.practiceSetupId!, p.practiceName ?? '']),
  );

  if (isLoading) {
    return (
      <BrandLoading size={48} label="Loading working hours…" />
    );
  }

  if (error) {
    return (
      <p className="text-sm text-status-error">
        Failed to load working hours. Please try again.
      </p>
    );
  }

  const profiles = data ?? [];
  const appointmentProfiles =
    apptProfilesQuery.data?.createAppointmentProfileRequest ?? [];
  const enabledLocations = apptProfilesQuery.data?.enabledLocation ?? [];
  const appointmentTemplates = apptTemplatesQuery.data?.request ?? [];
  const calendarSettings = calendarSettingsQuery.data ?? [];

  return (
    <div className="space-y-6">
      {/* Section order mirrors Flutter `doctor_schedule_tab.dart:572-740`:
       *   1. Calendar Settings (first)
       *   2. Working Hours
       *   3. Appointment Profiles
       *   4. Appointment Templates
       * The prior React order placed Calendar Settings last; reordered
       * to match Flutter parity per user ask 2026-05-20. */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className={SETTINGS_SECTION_TITLE}>
            Calendar settings
          </h2>
        </div>

        <CalendarSettingsSection
          isLoading={calendarSettingsQuery.isLoading}
          error={calendarSettingsQuery.error}
          settings={calendarSettings}
          practiceNameById={practiceNameById}
          onEdit={(id) => setEditingCalendarSettingsId(id)}
        />
      </div>

      <div className="border-t border-border-hairline pt-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className={SETTINGS_SECTION_TITLE}>
            Working hours
          </h2>
          <Button
            type="button"
            variant="tertiary"
            data-slot="settings-add"
            className="text-brand-primary"
            onClick={() => setIsCreating(true)}
          >
            <Plus className="size-4 mr-2" />
            Add Working Hours
          </Button>
        </div>

        {profiles.length === 0 ? (
          <p className="text-sm text-text-muted">
            No working hours configured for this practitioner.
          </p>
        ) : (
          <ul className="space-y-4">
            {profiles.map((profile) => (
              <WorkingHoursCard
                key={profile.workingHoursId}
                profile={profile}
                onEdit={() => setEditingId(profile.workingHoursId)}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-border-hairline pt-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className={SETTINGS_SECTION_TITLE}>
            Appointment profiles
          </h2>
          <Button
            type="button"
            variant="tertiary"
            data-slot="settings-add"
            className="text-brand-primary"
            onClick={() => setIsCreatingProfile(true)}
          >
            <Plus className="size-4 mr-2" />
            Add Profile
          </Button>
        </div>

        <AppointmentProfilesSection
          isLoading={apptProfilesQuery.isLoading}
          error={apptProfilesQuery.error}
          profiles={appointmentProfiles}
          onEdit={(id) => setEditingProfileId(id)}
        />
      </div>

      <div className="border-t border-border-hairline pt-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className={SETTINGS_SECTION_TITLE}>
            Appointment templates
          </h2>
          <Button
            type="button"
            variant="tertiary"
            data-slot="settings-add"
            className="text-brand-primary"
            onClick={() => setIsCreatingTemplate(true)}
          >
            <Plus className="size-4 mr-2" />
            Add Template
          </Button>
        </div>

        <AppointmentTemplatesSection
          isLoading={apptTemplatesQuery.isLoading}
          error={apptTemplatesQuery.error}
          templates={appointmentTemplates}
          profileNameById={profileNameById}
          onEdit={(id) => setEditingTemplateId(id)}
        />
      </div>

      {editingId && editingQuery.data && (
        <WorkingHoursEditDialog
          open={!!editingId}
          onOpenChange={(open) => {
            if (!open) setEditingId(null);
          }}
          initial={editingQuery.data}
          doctorSetupId={doctorSetupId}
        />
      )}

      {isCreating && (
        <WorkingHoursEditDialog
          open={isCreating}
          onOpenChange={(open) => {
            if (!open) setIsCreating(false);
          }}
          initial={null}
          doctorSetupId={doctorSetupId}
          practiceOptions={practicesQuery.data ?? []}
        />
      )}

      {editingCalendarSettingsId &&
        (() => {
          const editing = calendarSettings.find(
            (s) => s.id === editingCalendarSettingsId,
          );
          if (!editing || !orgId) return null;
          return (
            <CalendarSettingsEditDialog
              open
              onOpenChange={(open) => {
                if (!open) setEditingCalendarSettingsId(null);
              }}
              initial={editing}
              allSettings={calendarSettings}
              doctorSetupId={doctorSetupId}
              organizationId={orgId}
              practiceName={practiceNameById.get(editing.practiceId)}
            />
          );
        })()}

      {(editingTemplateId || isCreatingTemplate) && orgId && (
        <AppointmentTemplateEditDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setEditingTemplateId(null);
              setIsCreatingTemplate(false);
            }
          }}
          initial={
            editingTemplateId
              ? appointmentTemplates.find((t) => t.id === editingTemplateId) ?? null
              : null
          }
          allTemplates={appointmentTemplates}
          profiles={appointmentProfiles}
          practiceOptions={practicesQuery.data ?? []}
          doctorSetupId={doctorSetupId}
          organizationId={orgId}
        />
      )}

      {(editingProfileId || isCreatingProfile) && orgId && (
        <AppointmentProfileEditDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setEditingProfileId(null);
              setIsCreatingProfile(false);
            }
          }}
          initial={
            editingProfileId
              ? appointmentProfiles.find((p) => p.id === editingProfileId) ?? null
              : null
          }
          allProfiles={appointmentProfiles}
          enabledLocations={enabledLocations}
          practiceOptions={practicesQuery.data ?? []}
          doctorSetupId={doctorSetupId}
          organizationId={orgId}
        />
      )}
    </div>
  );
}

/**
 * Working-hours card — Flutter parity with `working_hour_list_item.dart`
 * (+ `workTimeSummary` in `scrips_core/utils.dart:688`). Layout, top→bottom:
 *   1. Profile name (bold)
 *   2. Work profile (`type === 1`): a `work` glyph + the working hours GROUPED
 *      BY SHARED TIME RANGE, each line showing a collapsed day summary
 *      ("Mon-Fri", "Mon-Tue, Thu") + " from {start} to {end}". "n/a" when empty.
 *      Leave profile (`type !== 1`): the `vacation` glyph + validity dates only.
 *   3. Work profile also shows a `vacation` glyph + validity date range.
 *   4. Practice (logo + name) LAST.
 * The previous React card listed one row per weekday and put the practice name
 * second with location chips — neither matches Flutter (removed for parity).
 */
function WorkingHoursCard({
  profile,
  onEdit,
}: {
  profile: DoctorWorkingHoursListItem;
  onEdit: () => void;
}) {
  const isWork = profile.type === 1;
  const groups = groupSlotsByTimeRange(profile.practiceWorkingSlots ?? []);
  const hasDates = Boolean(profile.startDate || profile.endDate);
  const dateRange = `${profile.startDate || '—'} - ${profile.endDate || '—'}`;

  return (
    <li data-slot="settings-record" data-record-size="lg" className="rounded-lg border border-border-card bg-surface-card shadow-1 px-4 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          <h3 data-slot="settings-record-title" className="text-base font-bold text-text-default">
            {profile.profileName || 'Untitled profile'}
          </h3>

          {isWork ? (
            <>
              {/* Working hours grouped by time range, day ranges collapsed. */}
              <div className="flex items-start gap-2">
                <Briefcase className="size-4 text-text-secondary mt-1 shrink-0" aria-hidden />
                <div className="text-sm text-text-default space-y-1">
                  {groups.length === 0 ? (
                    <span className="text-text-muted">n/a</span>
                  ) : (
                    groups.map((g, i) => (
                      <div key={`${g[0].startTime}-${g[0].endTime}-${i}`}>
                        <span className="font-semibold">{summarizeDays(g)}</span>
                        <span className="text-text-muted">
                          {` from ${g[0].startTime} to ${g[0].endTime}`}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Validity date range (Flutter renders this with the vacation
                  glyph on work profiles). */}
              {hasDates && (
                <div className="flex items-center gap-2 text-sm text-text-muted">
                  <TreePalm className="size-4 text-text-secondary shrink-0" aria-hidden />
                  <span>{dateRange}</span>
                </div>
              )}
            </>
          ) : (
            // Leave profile — just the vacation glyph + the leave date range.
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <TreePalm className="size-4 text-text-secondary shrink-0" aria-hidden />
              <span>{dateRange}</span>
            </div>
          )}

          {/* Practice (logo + name) — last, matching Flutter. */}
          <div className="flex items-center gap-2 text-sm text-text-default">
            {profile.practiceImageUrl ? (
              <img
                src={profile.practiceImageUrl}
                alt=""
                className="h-4 w-4 rounded-full object-cover shrink-0"
              />
            ) : (
              <MapPin className="size-4 text-text-secondary shrink-0" aria-hidden />
            )}
            <span className="truncate">{profile.practiceName || '—'}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${profile.profileName || 'working hours'}`}
          className="icon-action shrink-0"
        >
          <Pencil className="size-4" aria-hidden />
        </button>
      </div>
    </li>
  );
}

/** Group non-day-off slots by identical time range (Flutter `groupBy` on
 *  `"from {startTime} to {endTime}"`). Insertion order preserved. */
function groupSlotsByTimeRange(
  slots: DoctorWorkingHoursListSlot[],
): DoctorWorkingHoursListSlot[][] {
  const groups = new Map<string, DoctorWorkingHoursListSlot[]>();
  for (const s of slots) {
    if (s.dayOff) continue;
    const key = `${s.startTime}|${s.endTime}`;
    const arr = groups.get(key);
    if (arr) arr.push(s);
    else groups.set(key, [s]);
  }
  return Array.from(groups.values());
}

/**
 * Collapse a group's weekdays into Flutter's `workTimeSummary` form
 * (`scrips_core/utils.dart:688`): all 7 → "{first}-{last}"; runs of consecutive
 * `weedayId` → "Mon-Fri"; gaps → comma-separated runs ("Mon-Tue, Thu"). Uses
 * the 3-letter `startWeekDay` abbreviation like the Flutter source.
 */
function summarizeDays(slots: DoctorWorkingHoursListSlot[]): string {
  const sorted = [...slots].sort((a, b) => a.weedayId - b.weedayId);
  const start = (s: DoctorWorkingHoursListSlot) =>
    (s.startWeekDay || s.weekday || '').slice(0, 3);
  const end = (s: DoctorWorkingHoursListSlot) =>
    (s.endWeekDay || s.startWeekDay || s.weekday || '').slice(0, 3);
  if (sorted.length === 0) return '';
  if (sorted.length === 7)
    return `${start(sorted[0])}-${end(sorted[sorted.length - 1])}`;

  const parts: string[] = [];
  let runStart = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    const cur = sorted[i];
    const contiguous = cur != null && cur.weedayId === prev.weedayId + 1;
    if (!contiguous) {
      parts.push(
        runStart === prev ? start(runStart) : `${start(runStart)}-${start(prev)}`,
      );
      if (cur) runStart = cur;
    }
    if (cur) prev = cur;
  }
  return parts.join(', ');
}

function AppointmentProfilesSection({
  isLoading,
  error,
  profiles,
  onEdit,
}: {
  isLoading: boolean;
  error: unknown;
  profiles: AppointmentProfileEntry[];
  onEdit: (id: string) => void;
}) {
  if (isLoading) {
    return (
      <BrandLoading size={48} label="Loading appointment profiles…" />
    );
  }
  if (error) {
    return (
      <p className="text-sm text-status-error">
        Failed to load appointment profiles. Please try again.
      </p>
    );
  }
  if (profiles.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        No appointment profiles configured.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {profiles.map((p) => (
        <AppointmentProfileCard
          key={p.id}
          profile={p}
          onEdit={() => onEdit(p.id)}
        />
      ))}
    </ul>
  );
}

function AppointmentProfileCard({
  profile,
  onEdit,
}: {
  profile: AppointmentProfileEntry;
  onEdit: () => void;
}) {
  // `color` is Flutter `Color(int)` hex ("0xFFA7CAFF" — AARRGGBB).
  // Convert to CSS hex for the swatch; fall back to neutral if the
  // wire value is unrecognised.
  const cssColor = flutterColorToCss(profile.color) ?? 'var(--color-status-info-bg)';

  return (
    <li data-slot="settings-record" className="rounded-md border border-border-card bg-surface-card px-3 py-2.5 shadow-1">
      <div className="flex items-start gap-3">
        <span
          className="mt-1 inline-block h-3 w-3 rounded-full border border-border-hairline-strong flex-shrink-0 bg-[var(--pa-swatch-color)]"
          style={{ '--pa-swatch-color': cssColor } as CSSProperties}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p data-slot="settings-record-title" className="text-sm font-semibold text-text-default truncate">
              {profile.profileName || 'Untitled profile'}
            </p>
            {profile.isDefault && (
              <StatusChip tone="soft" semantic="info">
                Default
              </StatusChip>
            )}
            {profile.isPreferred && (
              <StatusChip tone="soft" semantic="info">
                Preferred
              </StatusChip>
            )}
            {profile.inAppBooking && (
              <StatusChip tone="soft" semantic="success">
                In-app booking
              </StatusChip>
            )}
          </div>
          <p className="mt-1 text-sm text-text-muted">
            {profile.minutesDuration} min ·{' '}
            {profile.location?.name ?? 'No location'}
            {profile.appointmentType?.display
              ? ` · ${profile.appointmentType.display}`
              : ''}
          </p>
          {profile.speciality?.code?.displayName && (
            <p className="mt-1 text-xs text-text-hint">
              {profile.speciality.code.displayName}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${profile.profileName || 'appointment profile'}`}
          className="icon-action flex-shrink-0"
        >
          <Pencil className="size-4" aria-hidden />
        </button>
      </div>
    </li>
  );
}

function AppointmentTemplatesSection({
  isLoading,
  error,
  templates,
  profileNameById,
  onEdit,
}: {
  isLoading: boolean;
  error: unknown;
  templates: AppointmentTemplateEntry[];
  profileNameById: Map<string, string>;
  onEdit: (id: string) => void;
}) {
  if (isLoading) {
    return (
      <BrandLoading size={48} label="Loading appointment templates…" />
    );
  }
  if (error) {
    return (
      <p className="text-sm text-status-error">
        Failed to load appointment templates. Please try again.
      </p>
    );
  }
  if (templates.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        No appointment templates configured.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {templates.map((t) => (
        <AppointmentTemplateCard
          key={t.id}
          template={t}
          profileName={profileNameById.get(t.appointmentProfileId)}
          onEdit={() => onEdit(t.id)}
        />
      ))}
    </ul>
  );
}

function AppointmentTemplateCard({
  template,
  profileName,
  onEdit,
}: {
  template: AppointmentTemplateEntry;
  /** Resolved from the cross-referenced appointment-profile cache. */
  profileName: string | undefined;
  onEdit: () => void;
}) {
  return (
    <li data-slot="settings-record" className="rounded-md border border-border-card bg-surface-card px-3 py-2.5 shadow-1">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p data-slot="settings-record-title" className="text-sm font-semibold text-text-default truncate">
              {template.templateName || 'Untitled template'}
            </p>
            {!template.isActive && (
              <StatusChip tone="soft" semantic="neutral">
                Inactive
              </StatusChip>
            )}
          </div>
          <p className="mt-1 text-sm text-text-muted">
            {formatTemplateTime(template.startTime)}
            {' – '}
            {formatTemplateTime(template.endTime)}
          </p>
          {template.weekDay && template.weekDay.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {template.weekDay.map((day) => (
                <StatusChip key={day} tone="soft" semantic="info">
                  {day.slice(0, 3)}
                </StatusChip>
              ))}
            </div>
          )}
          {/* Cross-reference: resolve profile name from cached
              appointment-profile list. Falls back to a short prefix of
              the GUID if the cache hasn't populated yet (cold cache /
              orphan reference). */}
          <p className="mt-1 text-xs text-text-hint">
            Profile:{' '}
            {profileName ?? `(${template.appointmentProfileId.slice(0, 8)}…)`}
          </p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${template.templateName || 'appointment template'}`}
          className="icon-action flex-shrink-0"
        >
          <Pencil className="size-4" aria-hidden />
        </button>
      </div>
    </li>
  );
}

function CalendarSettingsSection({
  isLoading,
  error,
  settings,
  practiceNameById,
  onEdit,
}: {
  isLoading: boolean;
  error: unknown;
  settings: CalendarSettings[];
  practiceNameById: Map<string, string>;
  onEdit: (id: string) => void;
}) {
  if (isLoading) {
    return (
      <BrandLoading size={48} label="Loading calendar settings…" />
    );
  }
  if (error) {
    return (
      <p className="text-sm text-status-error">
        Failed to load calendar settings. Please try again.
      </p>
    );
  }
  if (settings.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        No calendar settings configured.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {settings.map((s) => (
        <CalendarSettingsCard
          key={s.id}
          setting={s}
          practiceName={practiceNameById.get(s.practiceId)}
          onEdit={() => onEdit(s.id)}
        />
      ))}
    </ul>
  );
}

function CalendarSettingsCard({
  setting,
  practiceName,
  onEdit,
}: {
  setting: CalendarSettings;
  /** Resolved from the cached practitioner-practices list. */
  practiceName: string | undefined;
  onEdit: () => void;
}) {
  return (
    <li data-slot="settings-record" className="rounded-md border border-border-card bg-surface-card px-3 py-2.5 shadow-1">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p data-slot="settings-record-title" className="text-sm font-semibold text-text-default truncate">
            {/* Resolve practice name; fall back to short GUID prefix
                if the cross-tab cache hasn't populated (cold cache OR
                orphan reference). Same pattern as the template-card
                profile name resolver. */}
            {practiceName ?? `(${setting.practiceId.slice(0, 8)}…)`}
          </p>

          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            <dt className="text-text-muted">Slot increment</dt>
            <dd className="font-semibold text-text-default">
              {setting.defaultCalendarIncrement} min
            </dd>

            <dt className="text-text-muted">Patient slot window</dt>
            <dd className="font-semibold text-text-default">
              {setting.showAvailableSlotsToPatientDaysAhead} days ahead
            </dd>

            <dt className="text-text-muted">Practice slot window</dt>
            <dd className="font-semibold text-text-default">
              {setting.showAvailableSlotsToPracticeDaysAhead} days ahead
            </dd>

            <dt className="text-text-muted">Overbooking</dt>
            <dd className="font-semibold text-text-default">
              {setting.overBookingPerSlot} per slot
            </dd>
          </dl>

          <div className="mt-2 flex flex-wrap gap-2">
            <FeatureBadge
              label="Virtual"
              enabled={setting.allowVirtualAppointments}
            />
            <FeatureBadge label="Home visits" enabled={setting.allowHomeVisits} />
            {setting.allowHomeVisits && (
              <>
                <StatusChip tone="soft" semantic="info">
                  +{setting.homeVisitsDuration} min
                </StatusChip>
                <StatusChip tone="soft" semantic="info">
                  ≤{setting.maxDistance} km
                </StatusChip>
              </>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit calendar settings for ${practiceName ?? 'practice'}`}
          className="icon-action flex-shrink-0"
        >
          <Pencil className="size-4" aria-hidden />
        </button>
      </div>
    </li>
  );
}

function FeatureBadge({
  label,
  enabled,
}: {
  label: string;
  enabled: boolean;
}) {
  return (
    <StatusChip tone="soft" semantic={enabled ? 'success' : 'neutral'}>
      {label}: {enabled ? 'On' : 'Off'}
    </StatusChip>
  );
}
